import { useState, useRef, useContext } from "react"

// ** Third Party Components
import axios from "axios"
import { Delta } from "quill"
import { parseCookies } from "nookies"
import { useFormContext } from "react-hook-form"
import { Send, Paperclip, Plus } from "lucide-react"
import { Separator } from "@radix-ui/react-separator"
import { useMutation, useQueryClient } from "@tanstack/react-query"

// ** Custom Element
import { Label } from "../../label"
import { Button } from "../../button"
import { Progress } from "../../progress"
import { Dialog, DialogClose, DialogContent, DialogFooter } from "../../dialog"

// ** Schema
import { WorkLogFormData } from "@pma/utils/work-diaries-schema"

// ** Variables
import { DictType } from "@pma/types"
import { ACCESS_TOKEN, BLOCKED_EXTENSIONS } from "@pma/utils/constants"
import {
  CONTRACTOR_APPROVAL_ENDPOINT,
  SERVER_URL,
  SUPERVISOR_APPROVAL_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
} from "@pma/utils/api-routes"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** helpers
import { cn, parseMessage } from "@pma/utils/helpers"

// ** Custom Components
import RichEditorQuill from "../../rich-editor-quill"
import { RenderUploadPreview } from "@pma/components/files/upload-file"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

interface UserPicture {
  path: string
  name: string
  type: string
  size: number
  mime: string
  meta: {
    width: number
    height: number
  }
  url: string
}

interface User {
  id: number
  name: string
  email: string
  phone_number: string
  deleted: boolean
  user_picture: UserPicture
}

interface Comment {
  id: number
  created_at: number
  user_id: number
  organization_id: number
  note: string
  project_manager_main_id: number
  edit_at: number | null
  edit_by: number
  tagged_users: any[] // or a more specific type if known
  viewed_by: any[]
  liked_by: any[]
  reply_to_note: number
  project_id: number
  task_id: number
  diary_id: number
  _files: any[] // or a more specific type if known
  _is_contractor: boolean
  _is_financial: boolean
  _is_supervisor: boolean
  user: User
}
interface CommentsFormProps {
  workDiaries: DictType | undefined
  onCommentSubmit?: (comment: string) => void
  comments?: Comment[]
  refetchComment: () => void
  isLoading: boolean
}

export function CommentsForm({
  workDiaries,
  comments = [],
  refetchComment,
  isLoading,
}: CommentsFormProps) {
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const { translate, locale } = useTranslator()
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"]

  const diaryId = workDiaries?.id
  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const fileInputRef = useRef<HTMLInputElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)
  const allFiles = [...(workDiaries?.files_id || [])]
  const organizationUsers = authConfig?.organizationUsers

  const { userRoleDetails } = useContext(UserRoleContext)

  const { register, setValue, watch } = useFormContext<WorkLogFormData>()

  const isBothApproved = watch("contractorApproval") && watch("supervisorApproval")

  const [progress, setProgress] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<any[]>([])
  const [newComment, setNewComment] = useState("")
  const [uploadError, setUploadError] = useState(false)
  const [nameUpdates, setNameUpdates] = useState<string[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<DictType[]>([])

  const mentionUsers = organizationUsers
    ?.filter((user: DictType) => user?.name)
    .map((user: DictType) => ({
      id: user?.id,
      value: user?.name,
      url: user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`,
    }))

  const handleAddMoreFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleCancel = () => {
    setFile([])
    setProgress(0)
    setNameUpdates([])
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.files) {
      const fileArr = event.dataTransfer.files

      let validFileType = true
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (BLOCKED_EXTENSIONS.includes(fileExt)) {
          validFileType = false
          break
        }
      }

      if (validFileType) {
        setFile((prev) => [...prev, ...event.dataTransfer.files])
        setNameUpdates((prev) => [
          ...prev,
          ...[...event.dataTransfer.files].map((file) => file?.name),
        ])
      } else {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
      }
    }
  }

  const handleRemoveFile = (id: number) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id))
  }

  const uploadFile = async () => {
    if (!file || file.length === 0) {
      return
    }

    const formData = new FormData()

    const renamedFiles = file.map((val: File, index: number) => {
      const updatedName = nameUpdates[index] || val.name
      const fileNameArr = updatedName.split(".")
      const initialFileName = val.name.split(".")
      const fileExt = initialFileName[initialFileName.length - 1]

      return new File([val], `${fileNameArr[0]}.${fileExt}`, { type: val.type })
    })

    renamedFiles.forEach((renamedFile) => {
      formData.append("files", renamedFile)
    })

    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        setProgress(percentCompleted)
      },
    })

    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: async (data) => {
      if (data?.length > 0) {
        setUploadedFiles((prev) => [...prev, ...data])
        await uploadDiaryFiles(data)
        toast({
          title: translate("Files uploaded successfully"),
          description: translate("Files uploaded successfully"),
          variant: "success",
        })
        await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
        await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
        setIsOpen(false)
      }
    },
    onError: (error: DictType) => {
      setProgress(0)
      setUploadError(true)
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const uploadDiaryFiles = async (files: File[]) => {
    await axios.post(
      SERVER_URL + "/diary/files/add",
      {
        files_id: files.map((file: DictType) => Number(file.id)),
        diary_id: diaryId,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
  }

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return
    addNoteMutation.mutate(JSON.stringify(newComment))
  }

  const addNote = async (note: string) => {
    const response = await axios.post(
      SERVER_URL + "/notes/add",
      {
        note: JSON.stringify(note),
        context_id: diaryId,
        context_type: "diary",
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const addNoteMutation = useMutation({
    mutationKey: ["add-new-note"],
    mutationFn: (note: string) => addNote(newComment),
    onSuccess: async () => {
      setNewComment("")
      toast({
        title: translate("Success"),
        description: translate("Comment insert successfully"),
        variant: "success",
      })
      refetchComment()
      await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const handleCheckboxChange = async (
    name: "contractorApproval" | "supervisorApproval",
    value: boolean,
  ) => {
    setValue(name, value)
    const endpoint =
      name === "contractorApproval" ? CONTRACTOR_APPROVAL_ENDPOINT : SUPERVISOR_APPROVAL_ENDPOINT
    try {
      await axios.post(
        SERVER_URL + endpoint,
        { diary_id: diaryId, value },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      )
      await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    }
  }

  const handleChange = (e: Delta) => {
    const data = JSON.stringify(e.ops)
    setNewComment(data)
  }

  const handleUploadClick = () => {
    const hasEmptyName = nameUpdates.some((name) => name.trim() === "")
    if (hasEmptyName) {
      toast({
        title: translate("Error"),
        description: translate("Please enter a valid file name!"),
        variant: "error",
      })
      return
    }

    uploadFileMutation.mutate()
  }

  const handleFileChange = (event: any) => {
    setProgress(0)
    setUploadError(false)
    const extensionsArr = [
      "image/png",
      "image/webp",
      "image/jpeg",
      "image/pjpeg",
      "image/jpg",
      "video/mp4",
      "application/mp4",
      "video/quicktime",
      "video/x-quicktime",
      "image/mov",
      "audio/aiff",
      "audio/x-midi",
      "audio/x-wav",
      "video/avi",
      "audio/mpeg",
      "audio/mpeg3",
      "audio/x-mpeg-3",
      "audio/x-mpeg-3",
      "video/mpeg",
      "video/x-mpeg",
      "application/doc",
      "application/ms-doc",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/excel",
      "application/vnd.ms-excel",
      "application/x-excel",
      "application/x-msexcel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/mspowerpoint",
      "application/powerpoint",
      "application/vnd.ms-powerpoint",
      "application/x-mspowerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/pdf",
      "text/csv",
      "text/plain",
    ]
    if (event.target.files) {
      const fileArr = event.target.files

      let validFileType = true
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (
          BLOCKED_EXTENSIONS.includes(fileExt)
          // !extensionsArr.includes(fileArr[index].type.toLocaleLowerCase()) &&
          // !["ifc", "dwg"].includes(fileExt)
        ) {
          validFileType = false
          break
        }
      }
      if (validFileType) {
        setFile((prev) => [...prev, ...event.target.files])
        setNameUpdates((prev) => [...prev, ...[...event.target.files].map((file) => file?.name)])
      } else {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
      }
    }
  }

  return (
    <div className='space-y-6'>
      {/* Comments List */}
      <div className='space-y-4'>
        <h3 className='text-lg font-medium text-gray-900'>{translate("Comments")}</h3>

        <div className='max-h-50 space-y-4 overflow-y-auto pr-2'>
          {isLoading ? (
            <p className='py-4 text-center text-sm text-gray-500'>{`${translate("Loading")}...`}</p>
          ) : comments.length ? (<>
            {comments.map((comment: DictType) => {
              const isContractor = comment._is_contractor
              const roleBadge = comment._is_contractor
                ? { label: translate("Contractor"), color: "bg-orange-100 text-orange-700" }
                : comment._is_supervisor
                  ? { label: translate("Supervisor"), color: "bg-green-100 text-green-700" }
                  : comment._is_financial
                    ? { label: translate("Financial"), color: "bg-purple-100 text-purple-700" }
                    : null

              return (
                <div
                  key={comment.id}
                  className={cn(
                    "flex items-start gap-3",
                    isContractor ? "flex-row" : "flex-row-reverse",
                  )}
                >
                  <div className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600'>
                    {comment.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div
                    className={cn(
                      "max-w-[75%] rounded-lg p-3",
                      isContractor ? "bg-gray-50" : "bg-blue-50",
                    )}
                  >
                    <div className={cn("mb-1 flex items-center gap-2 flex-nowrap whitespace-nowrap")}>
                      <span className='text-sm font-medium text-gray-900'>{comment.user.name}</span>
                      {roleBadge && (
                        <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", roleBadge.color)}>
                          {roleBadge.label}
                        </span>
                      )}
                      <span className='text-xs text-gray-500'>
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: parseMessage(comment?.note, organizationUsers),
                      }}
                      className={cn(
                        "allow-list p-2 ps-3 text-xs text-slate-500",
                        isContractor
                          ? isHebrew ? "text-right" : "text-left"
                          : isHebrew ? "text-left" : "text-right",
                      )}
                    />
                  </div>
                </div>
              )
            })}</>
          ) : (
            <p className='py-4 text-center text-sm text-gray-500'>
              {`${translate("No comments yet")}. ${translate("Add the first one")}!`}
            </p>
          )}
          <div ref={commentsEndRef} />
        </div>

        {!isBothApproved && (
          <form className='mt-4'>
            <div className='flex items-start gap-2'>
              <div className='relative flex-1'>
                <div className='mx-1 mb-1 h-full min-h-[120px] max-w-[330px] border text-sm text-gray-600 shadow dark:text-gray-200 md:max-w-[800px]'>
                  <RichEditorQuill
                    placeHolder={translate("Write a reply")}
                    handleChange={handleChange}
                    id={diaryId.toString()}
                    mentionUsers={mentionUsers}
                    value={newComment}
                  />
                </div>
              </div>
              <button
                type='button'
                disabled={!newComment.trim() || addNoteMutation.isPending}
                className='inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50'
                onClick={handleCommentSubmit}
              >
                <Send className={cn("h-4 w-4", isHebrew ? "ml-1.5" : "mr-1.5")} />
                {translate("Send")}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* File Upload */}
      {!isBothApproved && (
        <div className='space-y-4'>
          <div className='max-w-4xl rounded-lg border border-gray-200 p-4 shadow-sm'>
            <div className='mb-4 flex items-center gap-2 text-lg font-semibold'>
              <Paperclip className='text-blue-600' />
              <span>{`${translate("Attachments")} (${translate("General")})`}</span>
            </div>

            <Button onClick={() => setIsOpen(!isOpen)}>{translate("Upload Media")}</Button>

            <div className='mt-4 space-y-2'>
              {allFiles.map((file: any) => {
                const isImage = imageExtensions.includes(file?.type)
                return (
                  <div
                    key={file.id}
                    className='relative flex items-center gap-3 rounded-md bg-gray-100 p-2'
                  >
                    {isImage ? (
                      <img
                        src={file?.file}
                        alt={file?.name}
                        className='h-16 w-16 rounded object-cover'
                      />
                    ) : (
                      <Paperclip className='h-5 w-5 text-blue-600' />
                    )}
                    <div className='flex-1 truncate'>
                      <a
                        href={file?.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        download={file?.name}
                        className='text-sm text-blue-600 hover:underline'
                      >
                        {file?.name}
                      </a>
                    </div>
                    {uploadedFiles.some((f) => f.id === file.id) && (
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        className='rounded px-2 py-1 text-sm text-red-600 hover:underline'
                        type='button'
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Approvals */}
      <div className='space-y-4 border-t pt-4'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:gap-6'>
          <label className='mb-2 flex cursor-pointer items-center gap-2 sm:mb-0'>
            <input
              type='checkbox'
              {...register("contractorApproval")}
              onChange={(e) => handleCheckboxChange("contractorApproval", e.target.checked)}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm font-medium text-gray-700'>
              {translate("Contractor Approval")}
            </span>
          </label>

          <label className='flex cursor-pointer items-center gap-2'>
            <input
              type='checkbox'
              {...register("supervisorApproval")}
              onChange={(e) => {
                if (userRoleDetails?.constructor)
                  handleCheckboxChange("supervisorApproval", e.target.checked)
              }}
              disabled={!userRoleDetails?.constructor}
              className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
            />
            <span className='text-sm font-medium text-gray-700'>
              {translate("Supervisor Approval")}
            </span>
          </label>
        </div>
      </div>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) handleCancel()
        }}
      >
        <DialogContent className='w-full max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-5xl'>
          <div className='flex w-full flex-col items-center justify-between'>
            <h4 className={cn("pb-2 font-semibold")}>
              {translate("File Upload")}{" "}
              <small>
                {file?.length > 0 && `(${file?.length} ${translate("files selected")})`}
              </small>
            </h4>
            <Separator className='w-full' />
          </div>
          <div className={`nested-draggable flex items-center justify-between`}>
            <input
              id='file-input'
              type='file'
              className='sr-only text-black'
              onChange={handleFileChange}
              multiple
              ref={fileInputRef}
            />
          </div>

          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
            className={cn(
              "my-0 flex flex-1 items-center justify-center rounded-md py-0 text-center",
              file.length > 0
                ? "overflow-y-auto border-none"
                : "border border-dashed border-primary/50",
            )}
          >
            <div className='grid h-[300px] w-full grid-cols-1 gap-2 p-0.5 sm:grid-cols-2 md:grid-cols-3'>
              {file?.length === 0 && (
                <p className='col-span-full flex w-full flex-col items-center justify-center text-muted-foreground'>
                  {translate("Drag and drop a file")}
                  <Button variant={"outline"} className='mt-2 flex justify-center'>
                    <Label htmlFor='file-input' className={cn("", isHebrew ? "rtl" : "rtl")}>
                      {translate("Choose Files")}
                    </Label>
                  </Button>
                </p>
              )}
              {file &&
                file?.map((file, index) => (
                  <RenderUploadPreview
                    file={file}
                    index={index}
                    setNameUpdates={setNameUpdates}
                    setFile={setFile}
                    nameUpdates={nameUpdates}
                    key={`${file.name}-${index}`}
                  />
                ))}
              {file?.length > 0 && (
                <div className='col-span-1 flex h-full w-full justify-center'>
                  <Button
                    variant='outline'
                    className='flex h-28 w-full items-center gap-2 border-dashed border-primary/50'
                    onClick={handleAddMoreFiles}
                  >
                    <Plus className='h-6 w-6 text-primary' />
                    <span>{translate("Add More Files")}</span>
                  </Button>
                </div>
              )}
            </div>{" "}
          </div>
          {progress !== 0 && (
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <p className='text-sm text-muted-foreground'>
                  {uploadError ? translate("Error") : translate("Uploading")}...
                </p>
                <p className='text-sm text-muted-foreground'>{progress}%</p>
              </div>
              <Progress value={progress} variant={uploadError ? "destructive" : "default"} />
            </div>
          )}
          <DialogFooter className='flex flex-row justify-evenly'>
            <DialogClose className='flex'>
              <Button variant='outline' onClick={handleCancel}>
                {translate("Cancel")}{" "}
              </Button>
            </DialogClose>
            <Button
              variant='default'
              disabled={uploadFileMutation?.isPending || file.length === 0}
              loading={uploadFileMutation?.isPending}
              onClick={handleUploadClick}
            >
              {translate("Upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
